/**
 * Portal checkout — mounted by the after-me/checkout-flow block.
 *
 *   cart page      1 Cart     update-item / remove-item, then on to Review
 *   checkout page  2 Review   read-only lines and totals, Confirm Order
 *                  3 Payment  amount + "Continue to PayNow" (or another enabled
 *                             method, picked from portal().gateways)
 *
 * Guests can place orders when WooCommerce guest checkout is enabled; billing
 * and shipping details are collected in the Payment step before submission.
 *
 * Continue to PayNow posts the Store API checkout with the zippy-pay PayNow
 * gateway and the confirmed billing/shipping details, then follows
 * payment_result.redirect_url: WooCommerce's order-pay page, which hands off
 * to the Zippy-hosted QR. Zippy's callback lands on order-received (step 4).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { PhoneInput } from "react-international-phone";
import "react-international-phone/style.css";
import { azConfig } from "@parent/js/frontend/core/config.js";
import { storeFetch } from "../portal/api.js";
import { decodeEntities, formatMoney } from "../portal/format.js";
import { emitCart, mountApp, QtyStepper, Steps } from "../portal/react.jsx";

const QTY_DELAY = 450;
const PAYNOW = "zippy_paynow_payment";

function portal() {
	return azConfig().portal || {};
}

function payNowId() {
	return portal().gateway || PAYNOW;
}

/** Methods the Payment step offers; PayNow alone when the config predates the list. */
function gateways() {
	const list = portal().gateways;
	if (Array.isArray(list)) return list;
	return portal().gatewayAvailable === false ? [] : [{ id: payNowId(), title: "PayNow", description: "" }];
}

function portalUrl(name, fallback = "/") {
	return portal().urls?.[name] || fallback;
}

const ADDRESS_FIELDS = [
	{ key: "first_name", label: "First name", autoComplete: "given-name" },
	{ key: "last_name", label: "Last name", autoComplete: "family-name" },
	{ key: "company", label: "Company", autoComplete: "organization", full: true },
	{ key: "country", label: "Country / Region", autoComplete: "country", type: "country", full: true },
	{ key: "address_1", label: "Street address", autoComplete: "address-line1", full: true },
	{ key: "address_2", label: "Apartment, suite, unit, etc.", autoComplete: "address-line2", full: true },
	{ key: "city", label: "Town / City", autoComplete: "address-level2" },
	{ key: "state", label: "State / County", autoComplete: "address-level1" },
	{ key: "postcode", label: "Postcode / ZIP", autoComplete: "postal-code" },
];

function emptyAddress() {
	return {
		first_name: "", last_name: "", company: "", address_1: "", address_2: "",
		city: "", state: "", postcode: "", country: "", email: "", phone: "",
	};
}

function configuredAddress(type) {
	return { ...emptyAddress(), ...(portal().addresses?.[type]?.value || {}) };
}

function requiredAddressKeys(address, type) {
	const fallback = ["first_name", "last_name", "country", "address_1", "postcode"];
	const keys = [...(portal().addressRequirements?.[address.country] || fallback)];
	if (type === "billing") {
		if (!keys.includes("phone")) keys.push("phone");
		if (!keys.includes("email")) keys.push("email");
	}
	return keys;
}

function missingAddressFields(address, type) {
	const labels = Object.fromEntries(
		[...ADDRESS_FIELDS, { key: "email", label: "Email address" }, { key: "phone", label: "Phone" }]
			.map((field) => [field.key, field.label]),
	);
	const missing = requiredAddressKeys(address, type)
		.filter((key) => !String(address[key] || "").trim())
		.map((key) => labels[key] || key);

	if (type === "billing" && address.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address.email)) {
		missing.push("a valid email address");
	}
	if (type === "billing" && address.phone) {
		const phone = String(address.phone).replace(/[\s().-]/g, "");
		if (!/^\+\d{7,15}$/.test(phone)) {
			missing.push("a valid phone number with country code");
		}
	}

	return missing;
}

function shippingFromBilling(billing) {
	return { ...emptyAddress(), ...billing, email: "", phone: "" };
}

function shippingPayload(address) {
	const { email, ...shipping } = address;
	return shipping;
}

function itemFields(item) {
	const ext = item.extensions?.["after-me"] || {};
	return {
		name: decodeEntities(item.name),
		pack: ext.pack_size || "",
		unit: ext.unit_label || "",
		// Chosen variation, e.g. "Color: Red, Logo: Yes".
		options: (item.variation || [])
			.map(({ attribute, value }) => `${decodeEntities(attribute)}: ${decodeEntities(value)}`)
			.join(", "),
		image: item.images?.[0]?.thumbnail || item.images?.[0]?.src || "",
	};
}

function Thumb({ src }) {
	return src ? <img className="amp-line__thumb" src={src} alt="" loading="lazy" /> : <span className="amp-line__thumb" />;
}

// ---- Step 1: cart line ------------------------------------------------------

function CartLine({ item, busy, onQuantity, onRemove }) {
	const { name, pack, unit, options, image } = itemFields(item);
	const limits = item.quantity_limits || {};
	const [qty, setQty] = useState(item.quantity);
	const timer = useRef(null);

	useEffect(() => {
		setQty(item.quantity);
	}, [item.quantity]);

	useEffect(() => () => clearTimeout(timer.current), []);

	const change = (next) => {
		setQty(next);
		clearTimeout(timer.current);
		timer.current = setTimeout(() => onQuantity(item.key, next), QTY_DELAY);
	};

	const unitPrice = formatMoney(item.prices?.price, item.prices);
	const meta = [options, pack, `${unitPrice}${unit ? ` / ${unit}` : ""}`].filter(Boolean).join(" · ");
	const lineTotal = (Number(item.prices?.price || 0) * qty).toString();

	return (
		<div className={`amp-line${busy ? " is-busy" : ""}`}>
			<Thumb src={image} />
			<div className="amp-line__info">
				<a className="amp-line__name" href={item.permalink}>
					{name}
				</a>
				<span className="amp-line__meta">{meta}</span>
			</div>
			<div className="amp-line__stepper">
				<QtyStepper
					size="sm"
					value={qty}
					min={limits.minimum || 1}
					max={limits.maximum || 9999}
					onChange={change}
					label={`Quantity of ${name}`}
					disabled={busy || limits.editable === false}
				/>
			</div>
			<span className="amp-line__total">{formatMoney(lineTotal, item.prices)}</span>
			<button
				type="button"
				className="amp-line__remove"
				aria-label={`Remove ${name}`}
				disabled={busy}
				onClick={() => onRemove(item.key)}
			>
				×
			</button>
		</div>
	);
}

function CartStep({ cart, busyKey, error, onQuantity, onRemove }) {
	const totals = cart.totals || {};

	return (
		<div className="amp-step-card">
			<Steps current={1} />
			<div className="amp-step-card__head">
				<h1 className="amp-step-card__title">Your cart</h1>
				<p className="amp-step-card__lead">Adjust quantities before you move to review.</p>
			</div>

			<div className="amp-lines">
				{cart.items.map((item) => (
					<CartLine
						key={item.key}
						item={item}
						busy={busyKey === item.key}
						onQuantity={onQuantity}
						onRemove={onRemove}
					/>
				))}
			</div>

			<div className="amp-totals">
				<div className="amp-totals__row amp-totals__row--grand">
					<span>Subtotal</span>
					<span>{formatMoney(totals.total_items, totals)}</span>
				</div>
			</div>

			{error && <ErrorNotice message={error} />}

			<div className="amp-step-card__actions">
				<a className="amp-link" href={portalUrl("shop")}>
					<span className="amp-arrow amp-arrow--back" aria-hidden="true">←</span> Continue shopping
				</a>
				<a
					className={`amp-btn${busyKey ? " is-disabled" : ""}`}
					href={portalUrl("checkout")}
					aria-disabled={busyKey ? "true" : undefined}
					onClick={(e) => busyKey && e.preventDefault()}
				>
					Proceed to Review
				</a>
			</div>
		</div>
	);
}

// ---- Step 2: review ---------------------------------------------------------

function Totals({ totals }) {
	const tax = Number(totals.total_tax || 0);
	const discount = Number(totals.total_discount || 0);

	return (
		<div className="amp-totals">
			<div className="amp-totals__row">
				<span>Subtotal</span>
				<span>{formatMoney(totals.total_items, totals)}</span>
			</div>
			{discount > 0 && (
				<div className="amp-totals__row">
					<span>Discount</span>
					<span>−{formatMoney(discount, totals)}</span>
				</div>
			)}
			{tax > 0 && (
				<div className="amp-totals__row">
					<span>Tax</span>
					<span>{formatMoney(tax, totals)}</span>
				</div>
			)}
			<div className="amp-totals__row amp-totals__row--grand">
				<span>Total</span>
				<span>{formatMoney(totals.total_price, totals)}</span>
			</div>
		</div>
	);
}

function ReviewStep({ cart, canCheckout, onConfirm }) {
	return (
		<div className="amp-step-card">
			<Steps current={2} />
			<div className="amp-step-card__head">
				<h1 className="amp-step-card__title">Review order</h1>
				<p className="amp-step-card__lead">Please check your items before placing the order.</p>
			</div>

			<div className="amp-lines">
				{cart.items.map((item) => {
					const { name, pack, options, image } = itemFields(item);
					const meta = [options, pack].filter(Boolean).join(" · ");
					return (
						<div key={item.key} className="amp-line">
							<Thumb src={image} />
							<div className="amp-line__info">
								<span className="amp-line__name">{name}</span>
								{meta && <span className="amp-line__meta">{meta}</span>}
							</div>
							<span className="amp-line__unit">{formatMoney(item.prices?.price, item.prices)}</span>
							<span className="amp-line__qty" aria-label="Quantity">
								{item.quantity}
							</span>
							<span className="amp-line__total">
								{formatMoney(item.totals?.line_subtotal, item.totals)}
							</span>
						</div>
					);
				})}
			</div>

			<Totals totals={cart.totals || {}} />

			{!canCheckout && (
				<p className="amp-notice" role="status">
					Guest checkout is unavailable. Sign in to place this order — your cart will be kept.
				</p>
			)}

			<div className="amp-step-card__actions">
				<a className="amp-link" href={portalUrl("cart")}>
					<span className="amp-arrow amp-arrow--back" aria-hidden="true">←</span> Back
				</a>
				{canCheckout ? (
					<button type="button" className="amp-btn" onClick={onConfirm}>
						Confirm Order
					</button>
				) : (
					<a className="amp-btn" href={portal().signInUrl || portalUrl("login")}>
						Sign in to place order
					</a>
				)}
			</div>
		</div>
	);
}

// ---- Step 3: payment --------------------------------------------------------

function PayNowIcon() {
	return (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
			<rect x="3" y="3" width="7" height="7" rx="1" />
			<rect x="14" y="3" width="7" height="7" rx="1" />
			<rect x="3" y="14" width="7" height="7" rx="1" />
			<path d="M14 14h3v3h-3zM20 14v.01M14 20h.01M17 17h3v3h-3" />
		</svg>
	);
}

function AddressForm({ type, value, disabled, onChange }) {
	const countries = portal().addressCountries || { SG: "Singapore" };
	const required = new Set(requiredAddressKeys(value, type));
	const fields = type === "billing"
		? [...ADDRESS_FIELDS, { key: "phone", label: "Phone", type: "phone", autoComplete: "tel" }, { key: "email", label: "Email address", type: "email", autoComplete: "email", full: true }]
		: ADDRESS_FIELDS;

	return (
		<div className="amp-address__form">
			<div className="amp-address__grid">
				{fields.map((field) => {
					const id = `amp-${type}-${field.key}`;
					const fullWidth = field.full || (type === "shipping" && field.key === "postcode");
					const fieldClass = `amp-address__field${fullWidth ? " amp-address__field--full" : ""}`;
					return (
						<div className={fieldClass} key={field.key}>
							<label className="amp-address__label" htmlFor={id}>
								{field.label}{required.has(field.key) && <span aria-hidden="true"> *</span>}
							</label>
							{field.type === "country" ? (
								<select
									id={id}
									name={`${type}_${field.key}`}
									value={value[field.key] || ""}
									autoComplete={`${type} ${field.autoComplete}`}
									required={required.has(field.key)}
									disabled={disabled}
									onChange={(event) => onChange({ ...value, [field.key]: event.target.value })}
								>
									<option value="">Select a country / region</option>
									{Object.entries(countries).map(([code, name]) => (
										<option value={code} key={code}>{decodeEntities(name)}</option>
									))}
								</select>
							) : field.type === "phone" ? (
								<PhoneInput
									defaultCountry={String(value.country || "SG").toLowerCase()}
									preferredCountries={["sg", "my", "vn"]}
									forceDialCode
									value={value[field.key] || ""}
									name={`${type}_${field.key}`}
									required={required.has(field.key)}
									disabled={disabled}
									className="amp-address__phone"
									inputProps={{ id, autoComplete: `${type} ${field.autoComplete}`, inputMode: "tel" }}
									onChange={(phone) => onChange({ ...value, [field.key]: phone })}
								/>
							) : (
								<input
									id={id}
									name={`${type}_${field.key}`}
									type={field.type || "text"}
									value={value[field.key] || ""}
									autoComplete={`${type} ${field.autoComplete}`}
									required={required.has(field.key)}
									disabled={disabled}
									onChange={(event) => onChange({ ...value, [field.key]: event.target.value })}
								/>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
}

function AddressSummary({ value }) {
	const countries = portal().addressCountries || {};
	const lines = [
		[value.first_name, value.last_name].filter(Boolean).join(" "),
		value.company,
		value.address_1,
		value.address_2,
		[value.city, value.state, value.postcode].filter(Boolean).join(" "),
		countries[value.country] || value.country,
	].filter(Boolean);

	return (
		<address className="amp-address__summary">
			{lines.map((line, index) => <span key={`${line}-${index}`}>{line}</span>)}
			{value.email && <span>{value.email}</span>}
			{value.phone && <span>{value.phone}</span>}
		</address>
	);
}

function AddressSection({ type, value, editing, disabled, onEdit, onCancel, onChange }) {
	const title = type === "billing" ? "Billing address" : "Shipping address";
	const canCancel = portal().addresses?.[type]?.complete === true;

	return (
		<section className="amp-address__card">
			<div className="amp-address__head">
				<div>
					<h2 className="amp-address__title">{title}</h2>
					<p className="amp-address__hint">
						{editing ? `Enter the ${type} details for this order.` : "Saved to your account."}
					</p>
				</div>
				{editing && canCancel ? (
					<button type="button" className="amp-address__edit" onClick={onCancel} disabled={disabled}>
						Cancel
					</button>
				) : !editing ? (
					<button type="button" className="amp-address__edit" onClick={onEdit} disabled={disabled}>
						Change
					</button>
				) : null}
			</div>
			{editing ? (
				<AddressForm
					type={type}
					value={value}
					disabled={disabled}
					onChange={onChange}
				/>
			) : <AddressSummary value={value} />}
		</section>
	);
}

function PaymentMethods({ methods, value, disabled, onChange }) {
	return (
		<fieldset className="amp-pay__methods" disabled={disabled}>
			<legend className="amp-pay__label">Payment method</legend>
			{methods.map((method) => (
				<label key={method.id} className={`amp-pay__method${method.id === value ? " is-selected" : ""}`}>
					<input
						type="radio"
						name="amp-payment-method"
						value={method.id}
						checked={method.id === value}
						onChange={() => onChange(method.id)}
					/>
					<span className="amp-pay__method-text">
						<span className="amp-pay__method-title">{decodeEntities(method.title)}</span>
						{method.description && (
							<span className="amp-pay__method-desc">{decodeEntities(method.description)}</span>
						)}
					</span>
				</label>
			))}
		</fieldset>
	);
}

function PaymentStep({
	cart, placing, error, method, billing, shipping, shipToDifferent, editingBilling, editingShipping,
	onMethod, onBilling, onShipping, onShipToDifferent, onEditBilling, onEditShipping,
	onCancelBilling, onCancelShipping, onBack, onPay,
}) {
	const totals = cart.totals || {};
	const methods = gateways();
	const available = methods.some((m) => m.id === method);
	// With nothing to pay by, the step still reads as PayNow's (with the notice).
	const payNow = !available || method === payNowId();

	return (
		<form
			className="amp-step-card amp-step-card--payment"
			onSubmit={(event) => {
				event.preventDefault();
				onPay();
			}}
		>
			<Steps current={3} />
			<div className="amp-step-card__head">
				<h1 className="amp-step-card__title">Payment</h1>
				<p className="amp-step-card__lead">Confirm your addresses and choose how you'd like to pay.</p>
			</div>

			<div className="amp-pay__addresses">
				<AddressSection
					type="billing"
					value={billing}
					editing={editingBilling}
					disabled={placing}
					onEdit={onEditBilling}
					onCancel={onCancelBilling}
					onChange={onBilling}
				/>
				<label className="amp-address__different">
					<input
						type="checkbox"
						checked={shipToDifferent}
						disabled={placing}
						onChange={(event) => onShipToDifferent(event.target.checked)}
					/>
					<span>Ship to another address</span>
				</label>
				<div
					className={`amp-address__shipping${shipToDifferent ? " is-open" : ""}`}
					aria-hidden={!shipToDifferent}
				>
					<div className="amp-address__shipping-inner">
						<AddressSection
							type="shipping"
							value={shipping}
							editing={editingShipping}
							disabled={placing || !shipToDifferent}
							onEdit={onEditShipping}
							onCancel={onCancelShipping}
							onChange={onShipping}
						/>
					</div>
				</div>
			</div>

			<div className="amp-pay__panel">
				{payNow && <PayNowIcon />}
				<div className="amp-pay__amount">
					<span className="amp-pay__label">Amount to pay</span>
					<span className="amp-pay__value">{formatMoney(totals.total_price, totals)}</span>
				</div>
				{payNow && available && <span className="amp-pay__note">Secure PayNow checkout by Zippy</span>}
			</div>

			{methods.length > 1 && (
				<PaymentMethods methods={methods} value={method} disabled={placing} onChange={onMethod} />
			)}

			{!available && (
				<p className="amp-notice amp-notice--error" role="alert">
					PayNow isn't available right now. Please try again later or contact the office.
				</p>
			)}
			{error && <ErrorNotice message={error} />}

			<button type="submit" className="amp-btn amp-btn--block amp-btn--lg" disabled={placing || !available}>
				{placing ? "Placing order…" : payNow ? "Continue to PayNow" : "Place order"}
			</button>
			<p className="amp-fineprint">
				{payNow
					? "Your order is placed when you continue. Orders are checked against bank records before dispatch."
					: "Your order is placed when you continue."}
			</p>
			<button type="button" className="amp-link" onClick={onBack} disabled={placing}>
				<span className="amp-arrow amp-arrow--back" aria-hidden="true">←</span> Back to review
			</button>
		</form>
	);
}

// ---- Shared states -----------------------------------------------------------

function ErrorNotice({ message }) {
	return (
		<p className="amp-notice amp-notice--error" role="alert">
			{message}
		</p>
	);
}

function EmptyCart({ current }) {
	return (
		<div className="amp-step-card amp-step-card--narrow">
			<Steps current={current} />
			<div className="amp-step-card__head">
				<h1 className="amp-step-card__title">Your cart is empty</h1>
				<p className="amp-step-card__lead">Browse the shop and add the supplies you need.</p>
			</div>
			<a className="amp-btn" href={portalUrl("shop")}>
				Go to Shop
			</a>
		</div>
	);
}

function LoadError({ current, message, onRetry }) {
	return (
		<div className="amp-step-card amp-step-card--narrow">
			<Steps current={current} />
			<div className="amp-step-card__head">
				<h1 className="amp-step-card__title">We couldn't load your cart</h1>
				<p className="amp-step-card__lead">{message}</p>
			</div>
			<button type="button" className="amp-btn" onClick={onRetry}>
				Try again
			</button>
		</div>
	);
}

function Loading({ current }) {
	return (
		<div className="amp-step-card" aria-busy="true">
			<Steps current={current} />
			<div className="amp-lines" aria-hidden="true">
				{[0, 1, 2].map((i) => (
					<div key={i} className="amp-line">
						<span className="amp-skeleton" style={{ width: "100%", height: 48 }} />
					</div>
				))}
			</div>
		</div>
	);
}

// ---- App ---------------------------------------------------------------------

const PAYMENT_HASH = "#payment";

function CheckoutApp({ config }) {
	const onCartPage = config.step === "cart";

	const [cart, setCart] = useState(null);
	const [loadError, setLoadError] = useState("");
	const [error, setError] = useState("");
	const [busyKey, setBusyKey] = useState("");
	const [placing, setPlacing] = useState(false);
	const [paying, setPaying] = useState(!onCartPage && window.location.hash === PAYMENT_HASH);
	const [method, setMethod] = useState(() => {
		const methods = gateways();
		return (methods.find((m) => m.id === payNowId()) || methods[0])?.id || "";
	});
	const [billing, setBilling] = useState(() => configuredAddress("billing"));
	const [shipping, setShipping] = useState(() => configuredAddress("shipping"));
	const [shipToDifferent, setShipToDifferent] = useState(
		() => portal().shipToDifferentDefault === true,
	);
	const [editingBilling, setEditingBilling] = useState(() => portal().addresses?.billing?.complete !== true);
	const [editingShipping, setEditingShipping] = useState(() => portal().addresses?.shipping?.complete !== true);

	const load = useCallback(async () => {
		setLoadError("");
		try {
			const data = await storeFetch("wc/store/v1/cart");
			setCart(data);
			emitCart(data);
		} catch (err) {
			setLoadError(err.message);
		}
	}, []);

	useEffect(() => {
		load();
	}, [load]);

	// Back/forward between Review and Payment.
	useEffect(() => {
		if (onCartPage) return undefined;
		const onPop = () => setPaying(window.location.hash === PAYMENT_HASH);
		window.addEventListener("popstate", onPop);
		return () => window.removeEventListener("popstate", onPop);
	}, [onCartPage]);

	const mutate = async (key, route, body) => {
		setBusyKey(key);
		setError("");
		try {
			const data = await storeFetch(`wc/store/v1/cart/${route}`, { method: "POST", body });
			setCart(data);
			emitCart(data);
		} catch (err) {
			setError(err.message);
			// Resync so the stepper shows what the cart really holds.
			load();
		} finally {
			setBusyKey("");
		}
	};

	const goPayment = () => {
		window.history.pushState(null, "", PAYMENT_HASH);
		setPaying(true);
		setError("");
		window.scrollTo({ top: 0, behavior: "smooth" });
	};

	const goReview = () => {
		if (window.location.hash === PAYMENT_HASH) window.history.back();
		setPaying(false);
		setError("");
	};

	const pay = async () => {
		const missingBilling = missingAddressFields(billing, "billing");
		const missingShipping = shipToDifferent ? missingAddressFields(shipping, "shipping") : [];
		if (missingBilling.length || missingShipping.length) {
			setEditingBilling(editingBilling || missingBilling.length > 0);
			setEditingShipping(editingShipping || missingShipping.length > 0);
			const section = missingBilling.length ? "billing" : "shipping";
			const fields = missingBilling.length ? missingBilling : missingShipping;
			setError(`Please check the ${section} address: ${fields.join(", ")}.`);
			return;
		}

		setPlacing(true);
		setError("");

		try {
			const result = await storeFetch("wc/store/v1/checkout", {
				method: "POST",
				headers: { "X-After-Me-Portal": "checkout" },
				body: {
					billing_address: billing,
					shipping_address: shippingPayload(
						shipToDifferent ? shipping : shippingFromBilling(billing),
					),
					payment_method: method,
					payment_data: [],
					customer_note: "",
				},
			});

			const payment = result?.payment_result || {};
			if (payment.payment_status === "failure" || payment.payment_status === "error") {
				const detail = (payment.payment_details || []).find((d) => d.key === "message");
				throw new Error(detail?.value || "Payment couldn't start. Please try again.");
			}

			window.location.assign(payment.redirect_url || portalUrl("orders"));
		} catch (err) {
			setError(err.message);
			setPlacing(false);
		}
	};

	const signedIn = portal().signedIn === true;
	const canCheckout = signedIn || portal().guestCheckout === true;
	const current = onCartPage ? 1 : paying && canCheckout ? 3 : 2;

	if (loadError) return <LoadError current={current} message={loadError} onRetry={load} />;
	if (!cart) return <Loading current={current} />;
	if (!cart.items?.length) return <EmptyCart current={current} />;

	if (onCartPage) {
		return (
			<CartStep
				cart={cart}
				busyKey={busyKey}
				error={error}
				onQuantity={(key, quantity) => mutate(key, "update-item", { key, quantity })}
				onRemove={(key) => mutate(key, "remove-item", { key })}
			/>
		);
	}

	if (paying && canCheckout) {
		return (
			<PaymentStep
				cart={cart}
				placing={placing}
				error={error}
				method={method}
				billing={billing}
				shipping={shipping}
				shipToDifferent={shipToDifferent}
				editingBilling={editingBilling}
				editingShipping={editingShipping}
				onMethod={(id) => {
					setMethod(id);
					setError("");
				}}
				onBilling={(value) => {
					setBilling(value);
					setError("");
				}}
				onShipping={(value) => {
					setShipping(value);
					setError("");
				}}
				onShipToDifferent={(checked) => {
					setShipToDifferent(checked);
					setError("");
				}}
				onEditBilling={() => setEditingBilling(true)}
				onEditShipping={() => setEditingShipping(true)}
				onCancelBilling={() => {
					setBilling(configuredAddress("billing"));
					setEditingBilling(false);
					setError("");
				}}
				onCancelShipping={() => {
					setShipping(configuredAddress("shipping"));
					setEditingShipping(false);
					setError("");
				}}
				onBack={goReview}
				onPay={pay}
			/>
		);
	}

	return <ReviewStep cart={cart} canCheckout={canCheckout} onConfirm={goPayment} />;
}

mountApp("portal-checkout", CheckoutApp);
